/**
 * pf-fastsimon.js — Parc Fermé headless Fast Simon integration.
 *
 * Loads the Fast Simon JavaScript SDK (window.FastSimonSDK — smartCollections /
 * fullTextSearch / event / trackViewportImpression) and renders results into our
 * own pf-card markup, per the documented "raw data, you render it" pattern
 * (fastsimon docs: getting-started/collection-page.mdx, getting-started/search-result-page.mdx).
 *
 * WHY WE LOAD THE SDK OURSELVES: the store's existing Fast Simon app embed
 * (product-filters-search/blocks/autocomplete) only ships the autocomplete-only
 * bundle (static-autocomplete.fastsimon.com/fast-simon-autocomplete-init.umd.js →
 * window.FastAutocomplete / window.FastSimonReporting). It does NOT expose
 * window.FastSimonSDK, so smartCollections()/fullTextSearch() are unavailable
 * until we add the general SDK ourselves. store_uuid / store_id below were read
 * directly from that embed's own network request (?UUID=...&store=...) on the
 * live storefront — they are public client-side identifiers, not secrets.
 *
 * RUNTIME QUIRK CONFIRMED 2026-08-19 (not documented): the shipped SDK build
 * (assets.fastsimon.com/sdk/latest) logs "params onProductsLoaded, onFacetsLoaded
 * missing" and makes NO network request if either callback is omitted — even when
 * facets:false. Both callbacks are therefore always passed below, regardless of
 * whether a given call actually wants facet data.
 *
 * PROGRESSIVE ENHANCEMENT / FALLBACK: every mount point starts hidden. The
 * server-rendered Liquid grid/facets/pagination stay visible and untouched until
 * a Fast Simon response successfully arrives — only then do we reveal the FS
 * containers and hide the fallback. If the SDK script fails to load, times out,
 * or errors, the Liquid version is simply what the shopper sees — nothing to undo.
 *
 * FAST SIMON SDK AUDIT FIXES 2026-10-25 (Lion Shpoliansky, Fast Simon CSM — "CMC X FS SDK Audit"):
 *  - #2 Search results now have the same filters + sort as collections (shared createFacetUI below),
 *       and the shopper's filter/sort choices are included in the "search" report.
 *  - #3 Sale page: the full filter list is shown again (see snippets/pf-sale-facet-fix.liquid) and the
 *       category / Under $50 chips stay in sync with it, so the two are one filter state, not two systems.
 *  - #5 reportCartVisited() — the cart drawer open report (called from assets/pf-fastsimon-shadow.js).
 *  - #6 Report names come from Fast Simon's official window.FastSimonEventName list first.
 *  - #7 Promo tiles render as cards, are listed in view reports and report their clicks.
 *  - #8 Filter rebuilds keep keyboard focus and open/closed state; result counts are announced to
 *       screen readers through a polite live region.
 */
(function () {
  'use strict';

  if (window.PFFastSimon) return;

  var FS_STORE_ID = 14510982;
  var FS_STORE_UUID = '71cfa029-7a65-481a-ba61-2bac5634fd84';
  var FS_SDK_URL = 'https://assets.fastsimon.com/sdk/latest/fast_simon_sdk.js';
  var READY_TIMEOUT_MS = 12000;
  var CART_ADD_RE = new RegExp('/cart/add');

  var TIER_LABELS = ['Sale 50%+', 'Sale 40%+', 'Sale 30%+', 'Sale 20%+'];
  /* 2026-10-14: narrow key for the tier tags. Verified live on the sale collection: [["Isp-tag-Discount","Sale 50%+"]]
     returns 62 / 40%+ 8 / 30%+ 15 / 20%+ 15, identical to the native /collections/sale/sale-NN tag pages, and a bogus
     value returns 0. The tier chips no longer wait for the Dashboard "Discount" facet to appear in the facet response. */
  var TIER_KEY = 'Isp-tag-Discount';

  function log() {
    if (window.__pfFastSimonVerbose) {
      console.log.apply(console, ['[pf-fastsimon]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  var readyCallbacks = [];
  var sdkReady = false;
  var sdkFailed = false;

  function onSdkReady(cb) {
    if (sdkReady) { cb(); return; }
    if (sdkFailed) return;
    readyCallbacks.push(cb);
  }

  function flushReady() {
    if (sdkReady) return;
    sdkReady = true;
    log('SDK ready');
    var cbs = readyCallbacks;
    readyCallbacks = [];
    cbs.forEach(function (cb) {
      try { cb(); } catch (e) { console.error('[pf-fastsimon]', e); }
    });
  }

  function failSdk(reason) {
    if (sdkReady || sdkFailed) return;
    sdkFailed = true;
    log('SDK unavailable — Liquid fallback stays visible.', reason);
    releasePending();
  }

  /* 2026-10-25 LOADING STATE (Rudy: "the sale page must look correct and be filterable — done correctly once and
     for all"). Root cause found: this file is deferred, so on these very long pages the SDK only started loading at
     DOMContentLoaded (~5s on the live sale page), and a hard 4s ready timeout then gave up for good — slower
     phones were left on the Liquid fallback (banded page 1, no Fast Simon filters) with nothing to undo it.
     Now snippets/pf-fastsimon-boot.liquid (rendered in <head> on collection/search pages) starts the SDK at the
     top of the page and puts <html> in a "pf-fs-pending" state, where the server-rendered grid shows as a Parc
     Fermé skeleton of the very same cards (no layout jump when Fast Simon's grid takes its place). This file
     joins that boot, releases the pending state the moment Fast Simon renders, and there is no hard give-up:
     if the boot's 10s failsafe shows the fallback first, Fast Simon still takes over when it arrives unless
     the shopper has already started using the fallback page. */
  // 2026-11-03 (Rudy: "the next page opens at the bottom of the page"): at 990px+ Horizon's base.css makes
  // .page-wrapper the scroller (html/body overflow hidden), so window.scrollTo was a no-op on desktop and
  // Prev/Next left shoppers at the bottom of the new page. Scroll whichever element actually scrolls, then
  // scroll again once the new page has rendered (the grid swap can move the target mid-scroll).
  function scrollerFor(el) {
    for (var n = el && el.parentElement; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
      var oy = getComputedStyle(n).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight + 1) return n;
    }
    return document.scrollingElement || document.documentElement;
  }
  function scrollToGrid(root) {
    var sc = scrollerFor(root);
    var isDoc = sc === document.scrollingElement || sc === document.documentElement;
    var top = root.getBoundingClientRect().top - (isDoc ? 0 : sc.getBoundingClientRect().top) + sc.scrollTop - 120;
    if (top < 0) top = 0;
    if (Math.abs(sc.scrollTop - top) < 4) return;
    try { sc.scrollTo({ top: top, behavior: 'smooth' }); } catch (e) { sc.scrollTop = top; }
  }
  function settleScroll(root) {
    if (!root.__pfScrollPending) return;
    root.__pfScrollPending = false;
    scrollToGrid(root);
  }

  function releasePending() {
    document.documentElement.classList.remove('pf-fs-pending');
  }

  var shopperEngaged = false;
  ['scroll', 'pointerdown', 'keydown'].forEach(function (t) {
    window.addEventListener(t, function () {
      if (!document.documentElement.classList.contains('pf-fs-pending') && window.pageYOffset > 300) shopperEngaged = true;
    }, { passive: true });
  });
  /* True when the fallback is on screen and the shopper is already using it: don't yank the page from under them. */
  function lateTakeoverBlocked() {
    var boot = window.__pfFsBoot;
    return !!(boot && boot.fellBack && shopperEngaged);
  }

  function ensureSdk() {
    var boot = window.__pfFsBoot;
    if (boot) {
      if (boot.ready) { flushReady(); return; }
      if (boot.failed) { failSdk('script error'); return; }
      if (!boot.joined) {
        boot.joined = true;
        boot.cbs.push(flushReady);
        boot.fails.push(function () { failSdk('script error'); });
      }
      return;
    }
    if (window.FastSimonSDK && typeof window.FastSimonSDK.smartCollections === 'function') {
      flushReady();
      return;
    }
    if (window.__pfFastSimonLoading) return;
    window.__pfFastSimonLoading = true;

    window.__fast_simon_sdk_config = {
      store_uuid: FS_STORE_UUID,
      store_id: FS_STORE_ID,
      type: 'MPA',
      withAttributes: true,
      onReady: flushReady
    };

    var script = document.createElement('script');
    script.src = FS_SDK_URL;
    script.async = true;
    script.onerror = function () { failSdk('script error'); };
    document.head.appendChild(script);

    // Pages without the head boot (product pages): reveal nothing, so only reporting waits on this.
    setTimeout(function () {
      if (!sdkReady) failSdk('timeout');
    }, READY_TIMEOUT_MS);
  }

  /* ANALYTICS FIX 2026-10-14 (Rudy: "make sure FastSimon analytics are working properly"): the SDK's
     event() dispatches on the wire VALUES of its FastEventName enum ("collection", "search",
     "productFromCollection", ...), not the enum KEYS this file was passing ("SmartCollectionPreformed",
     ...). Keys fell through the SDK's switch, so collection views, search views, product clicks and
     add-to-carts from these pages were never reported. Verified against assets.fastsimon.com/sdk/latest
     (t.FastEventName map + function S switch). Map keys to values here. */
  var FS_EVENT = {
    SmartCollectionPreformed: 'collection',
    SearchPerformed: 'search',
    CollectionProductClicked: 'productFromCollection',
    SerpProductClicked: 'productFromSearch',
    AddToCartPerformed: 'cartPerformed',
    CartVisited: 'cartVisited',
    CollectionPromoTileClicked: 'promoTileFromCollection',
    SerpPromoTileClicked: 'promoTileFromSearch'
  };

  /* Audit #6 (2026-10-25): resolve the wire name from Fast Simon's official list (window.FastSimonEventName,
     published by the SDK) first, then the SDK's own enum, and only then the hard-coded map above, so a future
     Fast Simon rename can't silently break reporting. */
  function fireEvent(eventName, data) {
    if (!window.FastSimonSDK || typeof window.FastSimonSDK.event !== 'function') return;
    var official = window.FastSimonEventName || window.FastSimonSDK.FastEventName || null;
    var wire = (official && official[eventName]) || FS_EVENT[eventName] || eventName;
    eventName = wire;
    try {
      window.FastSimonSDK.event({ eventName: eventName, data: data });
      log('event', eventName, data);
    } catch (e) {
      console.error('[pf-fastsimon] event error', e);
    }
  }

  function formatMoney(amount, currency) {
    var n = parseFloat(amount);
    if (isNaN(n)) return amount;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(n);
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }

  /* 2026-10-15 (Rudy: "the 2nd product image on hover is gone"): collections now render through Fast Simon
     (fastsimon_mode "always", Oct 14), and these JS cards never had the second image that
     snippets/pf-card-alt-img.liquid adds to the Liquid cards. Fast Simon's data carries only the main image
     (t2 is a cached copy of the same photo), so on the first hover the product's second image is read from
     Shopify's /products/<handle>.js and added as .pf-card__img--alt. It uses the same CSS fade as the Liquid
     cards. Desktop pointers only, nothing is fetched until a hover, and single-image products are skipped. */
  var ALT_OK = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  function armAltImage(card, media, url) {
    if (!ALT_OK || !url) return;
    function load() {
      card.removeEventListener('pointerenter', load);
      card.removeEventListener('focus', load);
      fetch(url.split('?')[0] + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p || !p.images || p.images.length < 2) return;
          var main = (p.featured_image || p.images[0] || '').split('?')[0];
          var src = null;
          for (var i = 0; i < p.images.length; i++) {
            if (p.images[i].split('?')[0] !== main) { src = p.images[i]; break; }
          }
          if (!src) return;
          if (src.indexOf('//') === 0) src = 'https:' + src;
          var alt = document.createElement('img');
          alt.className = 'pf-card__img pf-card__img--alt';
          alt.alt = '';
          alt.setAttribute('aria-hidden', 'true');
          alt.decoding = 'async';
          alt.addEventListener('load', function () { alt.classList.add('is-ready'); }, { once: true });
          alt.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=720';
          var first = media.querySelector('.pf-card__img');
          if (first) first.insertAdjacentElement('afterend', alt); else media.appendChild(alt);   // under the % OFF chip, as on the Liquid cards
        })
        .catch(function () {});
    }
    card.addEventListener('pointerenter', load);
    card.addEventListener('focus', load);
  }

  function renderProductCard(product, index, opts) {
    var a = document.createElement('a');
    a.href = product.u;
    a.className = 'pf-card pf-card--product';
    a.setAttribute('data-pf-card', '');
    a.setAttribute('data-pf-fs-product-id', product.id);
    if (product.sp_s === 'large') a.classList.add('pf-card--w2', 'pf-card--h2');
    else if (product.sp_s === 'horizontal') a.classList.add('pf-card--w2');

    var media = document.createElement('div');
    media.className = 'pf-card__media';

    var img = document.createElement('img');
    img.className = 'pf-card__img';
    img.loading = 'lazy';
    img.alt = product.l || '';
    img.src = product.t;
    if (product.t2) {
      img.addEventListener('error', function onErr() {
        img.removeEventListener('error', onErr);
        img.src = product.t2;
      });
    }
    media.appendChild(img);
    armAltImage(a, media, product.u);

    var price = parseFloat(product.p);
    var compare = product.p_c ? parseFloat(product.p_c) : 0;
    /* 2026-10-26: collection promo window (snippets/pf-collection-promo.liquid sets window.PF_COLL_PROMO while a
       collection-wide automatic discount is live, e.g. Cadillac F1 25% off for Mexico City GP). Shopify automatic
       discounts apply in the cart rather than to product.p, so cards show the in-cart promo price and badge. */
    /* 2026-10-28: preview-clock-aware collection promo expiry check. */
    var cpromo = window.PF_COLL_PROMO && (window.PF_PREVIEW_NOW || Date.now()) < Date.parse(window.PF_COLL_PROMO.end) ? window.PF_COLL_PROMO : null;
    if (cpromo) {
      var promoChip = document.createElement('span');
      promoChip.className = 'pf-chip pf-card__chip pf-chip--red pf-cpromo-chip';
      promoChip.textContent = cpromo.pct + '% OFF IN CART';
      media.appendChild(promoChip);
    } else if (compare > price) {
      var pct = Math.round(((compare - price) / compare) * 100);
      var chip = document.createElement('span');
      chip.className = 'pf-chip pf-card__chip ' + (pct >= 40 ? 'pf-chip--gold' : 'pf-chip--red');
      chip.textContent = pct + '% OFF';
      media.appendChild(chip);
    }

    var body = document.createElement('div');
    body.className = 'pf-card__body';

    var title = document.createElement('p');
    title.className = 'pf-card__title';
    title.textContent = product.l;

    var priceP = document.createElement('p');
    priceP.className = 'pf-mono pf-card__price';
    if (cpromo) {
      var promo = Math.round(price * (100 - cpromo.pct)) / 100;
      var promoFmt = formatMoney(String(promo), product.c);
      var wasFmt = formatMoney(compare > price ? product.p_c : product.p, product.c);
      var promoSpan = document.createElement('span');
      promoSpan.className = 'pf-cpromo-price';
      promoSpan.textContent = promoFmt;
      var wasSpan = document.createElement('span');
      wasSpan.className = 'pf-card__compare pf-cpromo-was';
      wasSpan.textContent = wasFmt;
      priceP.setAttribute('aria-label', 'Sale price ' + promoFmt + ', was ' + wasFmt);
      priceP.appendChild(promoSpan);
      priceP.appendChild(document.createTextNode(' '));
      priceP.appendChild(wasSpan);
    } else {
      priceP.appendChild(document.createTextNode(formatMoney(product.p, product.c)));
      if (compare > price) {
        var compareSpan = document.createElement('span');
        compareSpan.className = 'pf-card__compare';
        compareSpan.textContent = formatMoney(product.p_c, product.c);
        priceP.appendChild(document.createTextNode(' '));
        priceP.appendChild(compareSpan);
      }
    }

    body.appendChild(title);
    body.appendChild(priceP);
    a.appendChild(media);
    a.appendChild(body);

    if (opts && opts.clickEventName) {
      a.addEventListener('click', function () {
        fireEvent(opts.clickEventName, opts.clickEventData(product, index));
      });
    }

    requestAnimationFrame(function () {
      if (window.FastSimonSDK && typeof window.FastSimonSDK.trackViewportImpression === 'function') {
        window.FastSimonSDK.trackViewportImpression(a, product.id, index + 1);
      }
    });

    return a;
  }

  /* Audit #7 (2026-10-25): promo tiles. Fast Simon mixes merchandising tiles into results (product.promotile).
     They used to be skipped, so a tile created in the Fast Simon dashboard would never appear. They now render
     as a card in the same grid slot (same media box as a product card, so the grid stays uniform), are counted
     in the view report (promoTiles) and report their clicks with the official promo-tile events.
     2026-10-29: v2 — sizes, mobile image, custom HTML, audience rules, schedule guard, countdown (see SPEC). */
  /* Tile options travel in the tile's link query string (set in the Fast Simon dashboard):
     pf_tile=<key>  pf_aud=retail|all|guest|member|new  pf_start=<ISO>  pf_end=<ISO>  pf_action=klaviyo:<formId>
     Defaults: pf_aud=retail. Unknown values are ignored. */
  function tileOpts(href) {
    var o = { key: '', aud: 'retail', start: NaN, end: NaN, action: '', clean: href };
    if (!href) return o;
    try {
      var u = new URL(href, location.origin);
      var p = u.searchParams;
      o.key = p.get('pf_tile') || '';
      o.aud = (p.get('pf_aud') || 'retail').toLowerCase();
      o.start = Date.parse(p.get('pf_start') || '');
      o.end = Date.parse(p.get('pf_end') || '');
      o.action = p.get('pf_action') || '';
      ['pf_tile', 'pf_aud', 'pf_start', 'pf_end', 'pf_action'].forEach(function (k) { p.delete(k); });  /* 2026-10-29 peer review: no pf_* params in crawlable hrefs */
      o.clean = u.origin === location.origin ? u.pathname + u.search + u.hash : u.toString();
    } catch (e) {}
    return o;
  }

  function tileNow() { return window.PF_PREVIEW_NOW || Date.now(); }

  /* Klaviyo keeps the visitor's identity in the __kla_id cookie (base64 JSON); an $exchange_id / $email / $phone_number
     means Klaviyo already knows them, so a new-subscriber tile is pointless. Synchronous, so no layout shift. */
  function klIdentifiedSync() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]+)/);
      if (!m) return false;
      var raw = decodeURIComponent(m[1]);
      var txt = raw.charAt(0) === '{' ? raw : atob(raw);
      return /"\$(exchange_id|email|phone_number)"\s*:\s*"[^"]+"/.test(txt);
    } catch (e) { return false; }
  }

  /* false = do not render this tile for this shopper (sync rules only; Klaviyo check is async, see renderPromoTile). */
  function tileAllowed(o) {
    var now = tileNow();
    if (!isNaN(o.start) && now < o.start) return false;
    if (!isNaN(o.end) && now >= o.end) return false;
    /* 2026-10-29: never advertise the page the shopper is already on (e.g. the sale tile on the sale collection). */
    if (o.clean) {
      try {
        var tp = new URL(o.clean, location.origin);
        var tpp = tp.pathname.replace(/\/$/, ''), here = location.pathname.replace(/\/$/, '');
        if (tp.origin === location.origin && tpp && tpp !== '' && (here === tpp || here.indexOf(tpp + '/') === 0)) return false;
      } catch (e) {}
    }
    if (window.PF_WS && o.aud !== 'all') return false;          // B2B/wholesale never see retail promos
    if (o.aud === 'guest' && window.PF_CUSTOMER) return false;
    if (o.aud === 'member' && !window.PF_CUSTOMER) return false;
    if (o.aud === 'new') {
      if (window.PF_CUSTOMER) return false;
      try { if (localStorage.getItem('pf_kl_joined') === '1') return false; } catch (e) {}
      if (klIdentifiedSync()) return false;     /* decided before render, so the grid never shifts (peer review 2026-10-29) */
      if (window.PF_COLL_PROMO && tileNow() < Date.parse(window.PF_COLL_PROMO.end)) return false;  /* sale collection: one discount per order */
    }
    return true;
  }

  function fmtLeft(ms) {
    var m = Math.max(0, Math.floor(ms / 60000)), d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60), mm = m % 60;
    return d > 0 ? d + 'D ' + h + 'H' : (h > 0 ? h + 'H ' + mm + 'M' : mm + 'M');
  }

  /* Remember Klaviyo sign-ups so the welcome tile stops showing (Klaviyo dispatches 'klaviyoForms'). */
  if (!window.__pfKlListen) {
    window.__pfKlListen = true;
    window.addEventListener('klaviyoForms', function (e) {
      if (e && e.detail && (e.detail.type === 'submit' || e.detail.type === 'stepSubmit')) {
        try { localStorage.setItem('pf_kl_joined', '1'); } catch (err) {}
        var t = document.querySelectorAll('[data-pf-tile-aud="new"]');
        for (var i = 0; i < t.length; i++) t[i].remove();
      }
    });
  }

  /* Strip <script>, inline on* handlers and javascript: URLs from dashboard HTML tiles. */
  function safeTileHtml(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = String(html || '');
    var bad = tpl.content.querySelectorAll('script,iframe,object,embed');
    for (var i = 0; i < bad.length; i++) bad[i].remove();
    var all = tpl.content.querySelectorAll('*');
    for (var j = 0; j < all.length; j++) {
      var attrs = all[j].attributes;
      for (var k = attrs.length - 1; k >= 0; k--) {
        var n = attrs[k].name.toLowerCase(), v = String(attrs[k].value || '').trim().toLowerCase();
        if (n.indexOf('on') === 0 || ((n === 'href' || n === 'src' || n === 'xlink:href') && v.indexOf('javascript:') === 0)) all[j].removeAttribute(attrs[k].name);
      }
    }
    return tpl.content;
  }

  function renderPromoTile(tile, index, opts) {
    var rawHref = tile.u || tile.link || tile.url || '';
    /* Review 2026-10-25: only relative or http(s) links (a javascript: URL from the dashboard must never run). */
    if (rawHref && !/^(https?:\/\/|\/(?!\/))/i.test(rawHref)) rawHref = '';
    var o = tileOpts(rawHref);
    if (!tileAllowed(o)) return null;
    var href = rawHref ? o.clean : '';

    var root = document.createElement(href ? 'a' : 'div');
    if (href) root.href = href;
    root.className = 'pf-card pf-card--product pf-card--promo';
    root.setAttribute('data-pf-card', '');
    root.setAttribute('data-pf-fs-promo-id', tile.id);
    if (o.key) root.setAttribute('data-pf-tile', o.key);
    root.setAttribute('data-pf-tile-aud', o.aud);

    var w = parseInt(tile.width || tile.w || 1, 10);
    var h = parseInt(tile.height || tile.h || 1, 10);
    if (w >= 2) root.classList.add('pf-card--w2');
    if (h >= 2) root.classList.add('pf-card--h2');

    var label = tile.l || tile.title || '';
    var sep = label.indexOf(' | ');
    var name = sep !== -1 ? label.slice(0, sep) : label;
    var cta = sep !== -1 ? label.slice(sep + 3) : '';

    var media = document.createElement('div');
    media.className = 'pf-card__media pf-promo__media';
    var src = tile.t || tile.image || tile.img || '';
    var msrc = tile.mobile_image || tile.mi || '';
    if (typeof tile.html === 'string' && tile.html) {
      var htmlWrap = document.createElement('div');
      htmlWrap.className = 'pf-promo__html';
      htmlWrap.appendChild(safeTileHtml(tile.html));
      media.appendChild(htmlWrap);
    } else if (src) {
      var pic = document.createElement('picture');
      if (msrc) {
        var source = document.createElement('source');
        source.media = '(max-width: 749px)';
        source.srcset = msrc;
        pic.appendChild(source);
      }
      var img = document.createElement('img');
      img.className = 'pf-card__img';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.alt = name;
      img.src = src;
      pic.appendChild(img);
      media.appendChild(pic);
    }
    if (!isNaN(o.end) && o.end - tileNow() <= 7 * 24 * 3600 * 1000) {
      var count = document.createElement('span');
      count.className = 'pf-chip pf-card__chip pf-chip--red pf-promo__count';
      count.setAttribute('aria-hidden', 'true');
      count.textContent = 'ENDS IN ' + fmtLeft(o.end - tileNow());
      media.appendChild(count);
      var timer = setInterval(function () {
        if (!root.isConnected) { clearInterval(timer); return; }
        var left = o.end - tileNow();
        if (left <= 0) { clearInterval(timer); root.remove(); return; }
        count.textContent = 'ENDS IN ' + fmtLeft(left);
      }, 60000);
    }
    root.appendChild(media);

    var body = document.createElement('div');
    body.className = 'pf-card__body';
    var title = document.createElement('p');
    title.className = 'pf-card__title';
    title.textContent = name;
    var ctaEl = document.createElement('p');
    ctaEl.className = 'pf-mono pf-card__price pf-promo__cta';
    if (cta) ctaEl.textContent = cta;
    else ctaEl.innerHTML = '&nbsp;';
    body.appendChild(title);
    body.appendChild(ctaEl);
    root.appendChild(body);
    if (href) root.setAttribute('aria-label', name + (cta ? ' — ' + cta : ''));

    root.addEventListener('click', function (e) {
      if (opts && opts.clickEventName) {
        var data = opts.clickEventData(tile, index);
        data.thumbnail = src || undefined;
        data.link = href || undefined;
        fireEvent(opts.clickEventName, data);
      }
      if (o.action.indexOf('klaviyo:') === 0 && window._klOnsite) {
        e.preventDefault();
        window._klOnsite.push(['openForm', o.action.slice(8)]);
      }
    });

    if (o.aud === 'new' && window.klaviyo && typeof window.klaviyo.isIdentified === 'function') {
      try {
        var idRes = window.klaviyo.isIdentified();
        if (idRes && typeof idRes.then === 'function') {
          idRes.then(function (identified) { if (identified) root.remove(); }, function () {});
        } else if (idRes) {
          root.remove();
        }
      } catch (e) {}
    }

    requestAnimationFrame(function () {
      if (window.FastSimonSDK && typeof window.FastSimonSDK.trackViewportImpression === 'function') {
        window.FastSimonSDK.trackViewportImpression(root, tile.id, index + 1);
      }
    });

    return root;
  }

  /* Products + promo tiles into a grid. ctx.kind is 'collection' or 'search'. Positions are 1-based slots. */
  function renderResultsInto(fsGrid, items, ctx) {
    fsGrid.innerHTML = '';
    /* 2026-10-29 peer review: on thin result pages (< 12 products) show at most one promo tile, so apparel stays first. */
    var nProducts = 0, tilesShown = 0;
    (items || []).forEach(function (it) { if (!it.promotile) nProducts++; });
    var maxTiles = nProducts < 12 ? 1 : 3;   /* peer review 2026-10-29: max 3 promo tiles per page, 1 on thin pages */
    items.forEach(function (item, i) {
      if (item.promotile) {
        if (tilesShown >= maxTiles) return;
        var el = renderPromoTile(item, i, {
          clickEventName: ctx.kind === 'search' ? 'SerpPromoTileClicked' : 'CollectionPromoTileClicked',
          clickEventData: function (t, idx) {
            return ctx.kind === 'search'
              ? { query: ctx.term, productID: t.id, position: idx + 1 }
              : { collectionID: ctx.categoryID, productID: t.id, position: idx + 1 };
          }
        });
        if (el) { fsGrid.appendChild(el); tilesShown++; }
        return;
      }
      fsGrid.appendChild(renderProductCard(item, i, {
        clickEventName: ctx.kind === 'search' ? 'SerpProductClicked' : 'CollectionProductClicked',
        clickEventData: function (p, idx) {
          return ctx.kind === 'search'
            ? { query: ctx.term, productID: p.id, position: idx + 1 }
            : { collectionID: ctx.categoryID, productID: p.id, position: idx + 1 };
        }
      }));
    });
  }

  /* Ids for the view reports: products and promo tiles listed separately. */
  function reportIds(items) {
    var products = [], tiles = [];
    var nP = 0; (items || []).forEach(function (it) { if (!it.promotile) nP++; });
    var cap = nP < 12 ? 1 : 3;   /* same thin-page cap as renderResultsInto */
    (items || []).forEach(function (t) {
      if (t.promotile) {
        if (tiles.length < cap && tileAllowed(tileOpts(t.u || t.link || t.url || ''))) tiles.push(t.id);
      } else {
        products.push(t.id);
      }
    });
    return {
      products: products.length ? products.toString() : undefined,
      promoTiles: tiles.length ? tiles.toString() : undefined
    };
  }

  /* Audit #8: polite live region so screen readers hear the new result count after a filter / sort change. */
  function createStatus(fsGrid) {
    var status = document.createElement('p');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('data-pf-fs-status', '');
    fsGrid.insertAdjacentElement('beforebegin', status);
    return function announce(count) {
      status.textContent = '';
      setTimeout(function () { status.textContent = count + (count === 1 ? ' product' : ' products'); }, 60);
    };
  }

  /* URL STATE (2026-10-25, "filterable properly"): filters, sort and page now live in the address bar, so a
     filtered view survives refresh, Back from a product page and sharing a link — before, every one of those
     reset the grid to unfiltered. Format (readable, Shopify-safe):
       ?fs.<Fast Simon facet key>=<value>   repeatable, e.g. fs.Type=Hats&fs.Size=M&fs.Size=L&fs.Price_from_to=0-50
       &fs_sort=<sort>                      only when the shopper picked a sort
       &page=<n>                            Shopify's own param, so the no-JS fallback renders the same page
     Each shopper change is one history entry; Back/Forward re-apply the state. */
  var FS_PARAM = 'fs.';
  var FS_SORT_PARAM = 'fs_sort';

  function readUrlState() {
    var q = new URLSearchParams(location.search);
    var narrow = [];
    q.forEach(function (v, k) { if (k.indexOf(FS_PARAM) === 0 && v !== '') narrow.push([k.slice(FS_PARAM.length), v]); });
    var page = parseInt(q.get('page'), 10);
    return { narrowBy: narrow, sortBy: q.get(FS_SORT_PARAM) || null, page: page > 0 ? page : 1 };
  }

  function buildUrl(pathname, state, skipKey) {
    var q = new URLSearchParams(location.search);
    Array.from(q.keys()).forEach(function (k) {
      if (k.indexOf(FS_PARAM) === 0 || k === FS_SORT_PARAM || k === 'page') q.delete(k);
    });
    state.narrowBy.forEach(function (n) { if (n[0] !== skipKey) q.append(FS_PARAM + n[0], n[1]); });
    if (state.userPickedSort && state.sortBy) q.set(FS_SORT_PARAM, state.sortBy);
    if (state.page > 1) q.set('page', String(state.page));
    var qs = q.toString();
    return pathname + (qs ? '?' + qs : '') + location.hash;
  }

  function pushUrl(url) {
    if (url === location.pathname + location.search + location.hash) return;
    try { history.pushState({ pfFs: true }, '', url); } catch (e) {}
  }

  /**
   * Shared filter + sort UI (collections and, since audit #2 on 2026-10-25, search). Builds the sort row, the
   * phone "Filters" toggle and the facet list into fsFacets, and owns the narrow/sort state changes.
   *   opts.state          shared state object ({ page, narrowBy, sortBy, userPickedSort, announce })
   *   opts.load           re-query function
   *   opts.sortOptions    [[value, label], ...]
   * Rebuilds keep the open/closed state of each filter and the keyboard focus (audit #8).
   */
  function createFacetUI(fsFacets, opts) {
    var state = opts.state;
    var sortWrap = document.createElement('div');
    sortWrap.className = 'pf-fs-sort-wrap';
    var facetListWrap = document.createElement('div');
    facetListWrap.className = 'pf-fs-facet-list';
    fsFacets.appendChild(sortWrap);
    fsFacets.appendChild(facetListWrap);
    // 2026-10-22: phones get one "Filters" toggle; the facet list stays closed until tapped (CSS hides the toggle on desktop).
    var filterToggle = document.createElement('button');
    filterToggle.type = 'button';
    filterToggle.className = 'pf-fs-filter-toggle';
    filterToggle.setAttribute('aria-expanded', 'false');
    filterToggle.textContent = 'Filters';
    filterToggle.addEventListener('click', function () {
      var open = !fsFacets.classList.contains('is-open');
      fsFacets.classList.toggle('is-open', open);
      filterToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    /* 2026-10-29 desktop filter bar (Parc Fermé standard: edges align, uniform sizes). From 750px each filter is an
       equal-width tile in a grid whose rows end flush with the product grid, and its options open as a dropdown over
       the page instead of pushing the products down. One dropdown at a time; outside click or Escape closes it;
       a dropdown near the right edge opens leftwards. Phones keep the stacked Filters list. */
    var deskMq = window.matchMedia ? window.matchMedia('(min-width: 750px)') : { matches: true };
    facetListWrap.addEventListener('toggle', function (e) {
      var d = e.target;
      if (!deskMq.matches || !d || d.tagName !== 'DETAILS' || !d.open) return;
      facetListWrap.querySelectorAll('details[open]').forEach(function (o) { if (o !== d) o.open = false; });
      var opts = d.querySelector('.pf-fs-facet__options');
      if (!opts) return;
      d.classList.remove('pf-fs-facet--flip');
      var edge = facetListWrap.getBoundingClientRect().right;
      if (opts.getBoundingClientRect().right > edge + 1) d.classList.add('pf-fs-facet--flip');
    }, true);
    document.addEventListener('click', function (e) {
      if (!deskMq.matches) return;
      facetListWrap.querySelectorAll('details[open]').forEach(function (d) { if (!d.contains(e.target)) d.open = false; });
    });
    facetListWrap.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !deskMq.matches) return;
      var d = e.target.closest && e.target.closest('details[open]');
      if (!d) return;
      d.open = false;
      var sum = d.querySelector('summary');
      if (sum) sum.focus();
    });

    function toggleNarrow(key, value, on) {
      if (on) {
        state.narrowBy.push([key, value]);
      } else {
        state.narrowBy = state.narrowBy.filter(function (n) { return !(n[0] === key && n[1] === value); });
      }
      state.page = 1;
      state.announce = true;
      if (opts.onChange) opts.onChange();
      opts.load();
    }

    function setNarrow(key, value) {
      state.narrowBy = state.narrowBy.filter(function (n) { return n[0] !== key; });
      if (value != null) state.narrowBy.push([key, value]);
      state.page = 1;
      state.announce = true;
      if (opts.onChange) opts.onChange();
      opts.load();
    }

    function renderCheckboxFacet(facet) {
      var key = facet[0], values = facet[1] || [], labelText = facet[2];
      var details = document.createElement('details');
      details.className = 'pf-fs-facet';
      details.setAttribute('data-pf-facet-key', key);
      var summary = document.createElement('summary');
      summary.className = 'pf-label pf-fs-facet__label';
      summary.textContent = labelText;
      summary.setAttribute('data-pf-focus-key', 'summary|' + key);
      details.appendChild(summary);
      var list = document.createElement('div');
      list.className = 'pf-fs-facet__options';
      /* YOUTH SIZES (moved here from sections/pf-collection.liquid, 2026-10-25): "S-128"-style values read
         "Youth S (128 cm)" and sit after the adult sizes, ordered by cm, under a "Youth sizes" heading. The value
         sent to Fast Simon is unchanged. */
      var YOUTH = /^([A-Z0-9]{1,4})-(\d{2,3})$/;
      if (/size/i.test(String(labelText || key))) {
        var adult = [], youth = [];
        values.forEach(function (v) { (YOUTH.test(v[0]) ? youth : adult).push(v); });
        youth.sort(function (a, b) { return +YOUTH.exec(a[0])[2] - +YOUTH.exec(b[0])[2]; });
        values = adult.concat(youth);
      }
      var youthHeadDone = false;
      values.forEach(function (val) {
        var optLabel = val[0], count = val[1];
        var ym = YOUTH.exec(optLabel);
        if (ym && /size/i.test(String(labelText || key)) && !youthHeadDone) {
          youthHeadDone = true;
          var head = document.createElement('p');
          head.className = 'pf-fs-facet__group';
          head.textContent = 'Youth sizes';
          list.appendChild(head);
        }
        var wrap = document.createElement('label');
        wrap.className = 'pf-fs-facet__option';
        var input = document.createElement('input');
        input.type = 'checkbox';
        input.value = optLabel;
        input.setAttribute('data-pf-focus-key', key + '|' + optLabel);
        input.checked = state.narrowBy.some(function (n) { return n[0] === key && n[1] === optLabel; });
        input.addEventListener('change', function () { toggleNarrow(key, optLabel, input.checked); });
        wrap.appendChild(input);
        var span = document.createElement('span');
        span.className = 'pf-fs-facet__option-label';
        /* 2026-10-29: Fast Simon's In Stock / On Sale filter values read "True" / "False"; show shopper words (value sent unchanged). */
        var shown = (ym && youthHeadDone) ? 'Youth ' + ym[1] + ' (' + ym[2] + ' cm)' : optLabel;
        var facetName = String(key) + ' ' + String(labelText || '');
        if (/^(true|false)$/i.test(optLabel)) {
          var yes = /^true$/i.test(optLabel);
          if (/in.?stock/i.test(facetName)) shown = yes ? 'In stock' : 'Sold out';
          else if (/on.?sale/i.test(facetName)) shown = yes ? 'On sale' : 'Full price';
        }
        span.textContent = shown + ' (' + count + ')';
        wrap.appendChild(span);
        list.appendChild(wrap);
      });
      details.appendChild(list);
      return details;
    }

    function renderRangeFacet(base, pair) {
      /* Fast Simon sends Price_min / Price_max values as a flat [39] (verified 2026-10-25), not [[39]], so the old
         pair.min[1][0][0] read left both boxes empty. Accept either shape. */
      function edge(f) { var v = (f[1] || [])[0]; return Array.isArray(v) ? v[0] : (v == null ? '' : v); }
      var minVal = edge(pair.min);
      var maxVal = edge(pair.max);
      /* Keep the shopper's applied range in the inputs after the rebuild (it used to snap back to the full range). */
      var applied = null;
      state.narrowBy.forEach(function (n) { if (n[0] === base + '_from_to') applied = String(n[1]).split('-'); });
      var wrap = document.createElement('div');
      wrap.className = 'pf-fs-facet pf-fs-facet--range';
      var labelText = (pair.min[2] || base).replace(/_min$/i, '');
      var label = document.createElement('p');
      label.className = 'pf-label pf-fs-facet__label';
      label.textContent = labelText;
      wrap.appendChild(label);
      var row = document.createElement('div');
      row.className = 'pf-fs-facet__range-row';
      var minInput = document.createElement('input');
      minInput.type = 'number'; minInput.value = applied ? applied[0] : minVal; minInput.className = 'pf-fs-facet__range-input';
      minInput.setAttribute('aria-label', labelText + ' minimum');
      minInput.setAttribute('data-pf-focus-key', base + '|min');
      var maxInput = document.createElement('input');
      maxInput.type = 'number'; maxInput.value = applied ? applied[1] : maxVal; maxInput.className = 'pf-fs-facet__range-input';
      maxInput.setAttribute('aria-label', labelText + ' maximum');
      maxInput.setAttribute('data-pf-focus-key', base + '|max');
      var apply = document.createElement('button');
      apply.type = 'button';
      apply.className = 'pf-btn pf-btn--secondary pf-fs-facet__range-apply';
      apply.textContent = 'Apply';
      apply.setAttribute('data-pf-focus-key', base + '|apply');
      apply.addEventListener('click', function () {
        setNarrow(base + '_from_to', minInput.value + '-' + maxInput.value);
      });
      row.appendChild(minInput);
      row.appendChild(document.createTextNode('–'));
      row.appendChild(maxInput);
      row.appendChild(apply);
      wrap.appendChild(row);
      return wrap;
    }

    function renderFacetList(facets) {
      /* Audit #8: remember which filters were open and what had focus, so a rebuild doesn't throw keyboard
         and screen-reader users back to the top of the page or snap every filter shut. */
      var active = document.activeElement;
      var focusKey = active && facetListWrap.contains(active) ? active.getAttribute('data-pf-focus-key') : null;
      var openKeys = {};
      facetListWrap.querySelectorAll('details[open]').forEach(function (d) { openKeys[d.getAttribute('data-pf-facet-key')] = true; });

      facetListWrap.innerHTML = '';
      if (!facets || !facets.length) return;
      var split = detectRangePairs(facets);

      // A price range control already covers price, so drop a separate "Price" checkbox facet (it showed "Price" twice).
      var hasPriceRange = Object.keys(split.pairs).some(function (b) { return /price/i.test(b); });
      split.regular.forEach(function (facet) {
        if (hasPriceRange && /price/i.test(String(facet[2] || facet[0]))) return;
        var el = renderCheckboxFacet(facet);
        if (openKeys[facet[0]]) el.open = true;
        facetListWrap.appendChild(el);
      });
      Object.keys(split.pairs).forEach(function (base) {
        var pair = split.pairs[base];
        if (pair.min && pair.max) facetListWrap.appendChild(renderRangeFacet(base, pair));
      });
      /* Desktop grid: a filter tile = 1 unit, a price range = 2. Wide screens fit all units on one row (up to 10);
         otherwise two full rows. */
      var ranges = facetListWrap.querySelectorAll('.pf-fs-facet--range');
      var units = facetListWrap.querySelectorAll('details.pf-fs-facet').length + 2 * ranges.length;
      var priceSpan = 2;
      if (units % 2 && ranges.length) { priceSpan = 3; units += 1; }   // an odd count widens the price tile so both rows end flush
      facetListWrap.style.setProperty('--pf-fs-price-span', String(priceSpan));
      var half = Math.max(1, Math.ceil(units / 2));
      facetListWrap.style.setProperty('--pf-fs-cols', String(half));
      facetListWrap.style.setProperty('--pf-fs-cols-wide', String(units <= 10 ? Math.max(units, 1) : half));

      if (focusKey) {
        var all = facetListWrap.querySelectorAll('[data-pf-focus-key]');
        for (var i = 0; i < all.length; i++) {
          if (all[i].getAttribute('data-pf-focus-key') === focusKey) {
            try { all[i].focus({ preventScroll: true }); } catch (e) { all[i].focus(); }
            break;
          }
        }
      }
    }

    function renderSortControl() {
      sortWrap.innerHTML = '';
      sortWrap.appendChild(filterToggle);
      var label = document.createElement('label');
      label.className = 'pf-label pf-fs-sort__label';
      label.textContent = 'Sort';
      var select = document.createElement('select');
      select.className = 'pf-fs-sort__select';
      select.setAttribute('aria-label', 'Sort');
      opts.sortOptions.forEach(function (opt) {
        var o = document.createElement('option');
        o.value = opt[0];
        o.textContent = opt[1];
        if (opt[0] === (state.sortBy || '')) o.selected = true;
        select.appendChild(o);
      });
      select.addEventListener('change', function () {
        state.sortBy = select.value || undefined;
        state.userPickedSort = true;
        state.page = 1;
        state.announce = true;
        if (opts.onChange) opts.onChange();
        opts.load();
      });
      label.appendChild(select);
      sortWrap.appendChild(label);
    }

    return {
      toggleNarrow: toggleNarrow,
      setNarrow: setNarrow,
      renderFacetList: renderFacetList,
      renderSortControl: renderSortControl
    };
  }

  var COLLECTION_SORTS = [
    ['', 'Recommended'],
    ['discount_max_to_min', 'Deepest discount'],
    ['discount_min_to_max', 'Smallest discount'],
    ['price_min_to_max', 'Price: low to high'],
    ['price_max_to_min', 'Price: high to low'],
    ['creation_date', 'Newest'],
    ['popularity', 'Popularity'],
    ['a_to_z', 'Name: A–Z'],
    ['z_to_a', 'Name: Z–A']
  ];
  /* Search keeps Fast Simon's relevance ranking as the default ("Best match"). */
  var SEARCH_SORTS = [['', 'Best match']].concat(COLLECTION_SORTS.slice(1));

  /* 2026-10-29 "Newest" = newest ADDED to the site (Shopify product creation), on collections and search.
     Fast Simon's creation_date sort orders by its c_date, which is Shopify's published_at: a product that is
     re-published (e.g. back in stock, or restored after the fire) jumped to the top as if it were brand new
     (Oct 27: 23 McLaren / F1 Tech items created Feb–Jun 2026 led /collections/newest-arrivals). Shopify product
     IDs rise with creation time, so for this sort the whole Fast Simon result set (filters still applied by
     Fast Simon) is fetched once with creation_date, re-ordered by product ID (newest created first) and paged
     in the browser. Collections index well under NEWEST_POOL items (newest-arrivals: ~400). The fetched set is
     cached per filter state, so Prev / Next / Back don't re-query. */
  var NEWEST_SORT = 'creation_date';
  var NEWEST_POOL = 1000;
  function orderNewest(items) {
    var products = [], tiles = [];
    (items || []).forEach(function (p, i) { if (p && p.promotile) tiles.push([i, p]); else products.push([p, i]); });
    products.sort(function (a, b) {
      var d = (Number(b[0] && b[0].id) || 0) - (Number(a[0] && a[0].id) || 0);
      return d || a[1] - b[1];
    });
    var out = products.map(function (x) { return x[0]; });
    /* Promo tiles keep the slot Fast Simon gave them. */
    tiles.forEach(function (t) { out.splice(Math.min(t[0], out.length), 0, t[1]); });
    return out;
  }
  function newestPager() {
    /* Peer review 2026-10-29, rounds 1-3 (Gemini, Grok, ChatGPT, Muse):
       - small per-filter cache (A → B → back to A doesn't refetch); facets are cached with the products and
         replayed on a cache hit, so the filter list always matches the grid;
       - every request snapshots its filters; answers (products AND facets) only reach the screen while that request
         is still the active one and the shopper still wants Newest with the same filters, so a late answer never
         paints over another sort or filter;
       - result sets bigger than NEWEST_POOL are completed page by page before sorting; an incomplete set is shown
         but never cached; a per-page watchdog keeps a hung page from blocking the next click. */
    var cache = {}, order = [], pending = null, active = null, cur = null;
    function keyOf(base, narrowBy) {
      var narrow = (narrowBy || []).map(function (n) { return JSON.stringify(n); }).sort();
      return JSON.stringify([base, narrow]);   // everything sent except page/sort
    }
    function wanted(key) { return !!cur && cur.state.sortBy === NEWEST_SORT && keyOf(cur.base, cur.state.narrowBy) === key; }
    function remember(key, entry) {
      if (!cache[key]) order.push(key);
      cache[key] = entry;
      while (order.length > 6) delete cache[order.shift()];
    }
    function isProduct(p) { return p && !p.promotile; }
    function serve(entry) {
      var state = cur.state, per = state.productsPerPage || 24, total = entry.products.length;
      var start = (state.page - 1) * per;
      cur.onProducts({
        products: entry.products.slice(start, start + per),
        totalResults: entry.count,
        page: state.page,
        pageCount: Math.max(1, Math.ceil(total / per)),
        turbolink: entry.turbolink
      });
    }
    return {
      run: function (call, base, state, onFacets, onProducts, fallback) {
        cur = { base: base, state: state, onFacets: onFacets, onProducts: onProducts, fallback: fallback };
        var key = keyOf(base, state.narrowBy);
        if (cache[key]) {
          active = cache[key];
          if (active.facets) cur.onFacets(active.facets);
          serve(active);
          return;
        }
        if (pending && pending.key === key) {   // in flight: its answer serves the latest caller
          active = pending;
          if (pending.facets) cur.onFacets(pending.facets);
          return;
        }
        var narrowSnap = JSON.parse(JSON.stringify(state.narrowBy || []));
        var req = pending = active = { key: key, facets: null, timer: 0 };
        var collected = [], productCount = 0, turbolink = null, seen = {}, ids = {};
        /* Round 4 (ChatGPT): retire this request before falling back, so its late facets can't overwrite the fallback's. */
        function giveUp() {
          var mine = active === req;
          if (mine) active = null;
          if (mine && wanted(key) && cur.fallback) cur.fallback();
        }
        function watchdog() {
          clearTimeout(req.timer);
          req.timer = setTimeout(function () {
            if (pending !== req) return;
            pending = null;
            log('Newest: Fast Simon did not answer in time; using Fast Simon paging.');
            giveUp();
          }, 20000);
        }
        function finish(complete) {
          clearTimeout(req.timer);
          pending = null;
          if (!complete) {
            /* Round 4 (ChatGPT): a partial pool can't be ordered reliably (a re-published old item can sit ahead of an
               unfetched newer one), so this filter state goes to Fast Simon's own paging instead. Not expected in
               practice: the whole index is ~400 items. */
            log('Newest: result set incomplete (' + productCount + ' fetched); using Fast Simon paging.');
            giveUp();
            return;
          }
          var entry = { products: orderNewest(collected), count: productCount, turbolink: turbolink, facets: req.facets };
          req.entry = entry;
          remember(key, entry);
          log('Newest: ' + entry.products.length + ' items re-ordered by creation (product ID).');
          if (active === req) active = entry;
          if (active === entry && wanted(key)) serve(entry);
        }
        function fetchPage(pageNum) {
          var args = {};
          for (var k in base) if (Object.prototype.hasOwnProperty.call(base, k)) args[k] = base[k];
          args.page = pageNum;
          args.productsPerPage = NEWEST_POOL;
          args.narrowBy = narrowSnap;
          args.sortBy = NEWEST_SORT;
          /* The SDK silently skips a call made with facets:false, so every page asks for facets; only page 1's are used. */
          args.facets = true;
          args.onFacetsLoaded = function (response) {
            if (pageNum !== 1) return;
            req.facets = response;
            if (req.entry) req.entry.facets = response;   // facets can arrive after the products
            if ((active === req || active === req.entry) && wanted(key)) cur.onFacets(response);
          };
          args.onProductsLoaded = function (response) {
            if (pending !== req) return;   // superseded, or the watchdog already gave up
            if (response && response.products && seen[pageNum]) return;   // the SDK can deliver a page twice (products, then facets+products)
            if (!response || !response.products) {
              clearTimeout(req.timer);
              pending = null;
              var mine = active === req;
              if (mine) active = null;   // round 4 (ChatGPT): a failed request's late facets must not paint
              if (mine && wanted(key)) cur.onProducts(response);
              return;
            }
            seen[pageNum] = true;
            if (pageNum === 1) turbolink = response.turbolink;
            /* Round 4 (Muse): de-duplicate by product ID across pages, in case the index shifts between pages. */
            var got = 0;
            response.products.forEach(function (p) {
              if (!isProduct(p)) { if (pageNum === 1) collected.push(p); return; }
              if (ids[p.id]) return;
              ids[p.id] = 1; collected.push(p); got++;
            });
            productCount += got;
            var total = response.totalResults || 0;
            if (productCount >= total) { finish(true); return; }
            if (!got || pageNum >= 10) { finish(false); return; }
            watchdog();
            fetchPage(pageNum + 1);
          };
          call(args);
        }
        watchdog();
        fetchPage(1);
      }
    };
  }

  function detectRangePairs(facets) {
    var pairs = {};
    var regular = [];
    facets.forEach(function (facet) {
      var key = facet[0];
      if (/_min$/.test(key) || /_max$/.test(key)) {
        var base = key.replace(/_min$|_max$/, '');
        pairs[base] = pairs[base] || {};
        pairs[base][/_min$/.test(key) ? 'min' : 'max'] = facet;
      } else {
        regular.push(facet);
      }
    });
    return { pairs: pairs, regular: regular };
  }

  function findTierFacet(facets) {
    var found = null;
    facets.forEach(function (facet) {
      var values = facet[1] || [];
      var hasTier = values.some(function (v) { return TIER_LABELS.indexOf(v[0]) !== -1; });
      if (hasTier) found = facet;
    });
    return found;
  }

  function mountCollection(root) {
    var categoryID = root.getAttribute('data-collection-id');
    if (!categoryID) return;

    var isSale = root.getAttribute('data-is-sale') === 'true';
    var fsGrid = root.querySelector('[data-pf-fs-grid]');
    var fsFacets = root.querySelector('[data-pf-fs-facets]');
    var fallbackEls = root.querySelectorAll('[data-pf-fs-fallback]');
    var tierNav = root.querySelector('[data-pf-tier-nav]');
    var catNav = root.querySelector('[data-pf-cat-nav]');
    if (!fsGrid) return;

    /* 2026-10-14: "New Arrivals" (now in the main menu) opens newest first instead of Fast Simon's "Recommended". */
    var defaultSort = isSale ? 'discount_max_to_min'
      : (/^(newest-arrivals|new-arrivals|2026-new-arrivals)$/.test(root.getAttribute('data-collection-handle') || '') ? 'creation_date' : undefined);

    /* Sale tiers stay in the path (/collections/sale/sale-50); everything else comes from the query string. */
    function tierFromPath() {
      var m = location.pathname.match(/\/sale-(\d0)\/?$/);
      var label = m ? 'Sale ' + m[1] + '%+' : null;
      return label && TIER_LABELS.indexOf(label) !== -1 ? label : null;
    }
    function stateFromUrl() {
      var u = readUrlState();
      var t = isSale ? (tierFromPath() || root.getAttribute('data-pf-fs-tier')) : null;
      var narrow = (t && TIER_LABELS.indexOf(t) !== -1) ? [[TIER_KEY, t]] : [];
      u.narrowBy.forEach(function (n) { if (n[0] !== TIER_KEY) narrow.push(n); });
      return { narrowBy: narrow, sortBy: u.sortBy || defaultSort, userPickedSort: !!u.sortBy, page: u.page };
    }
    function collectionPath() {
      var base = location.pathname.replace(/\/sale-\d0\/?$/, '');
      if (!isSale) return base;
      var tier = null;
      state.narrowBy.forEach(function (n) { if (n[0] === TIER_KEY) tier = n[1]; });
      var m = tier && tier.match(/(\d0)%/);
      return base + (m ? '/sale-' + m[1] : '');
    }
    function commitUrl() { pushUrl(buildUrl(collectionPath(), state, TIER_KEY)); }

    var initial = stateFromUrl();
    var state = {
      page: initial.page,
      productsPerPage: parseInt(root.getAttribute('data-products-per-page'), 10) || 24,
      narrowBy: initial.narrowBy,
      sortBy: initial.sortBy,
      userPickedSort: initial.userPickedSort,
      announce: false
    };

    var ui = fsFacets ? createFacetUI(fsFacets, { state: state, load: function () { load(); }, onChange: commitUrl, sortOptions: COLLECTION_SORTS }) : null;
    var announce = createStatus(fsGrid);

    function revealFsUI(force) {
      if (root.getAttribute('data-fs-active') === 'true') return true;
      /* Review 2026-10-25: the first Fast Simon answer can arrive after the 10s failsafe; don't swap the grid under
         a shopper who is already using the fallback page. */
      if (!force && lateTakeoverBlocked()) { log('Late Fast Simon answer; the shopper is using the fallback page, leaving it.'); return false; }
      fsGrid.hidden = false;
      if (fsFacets) fsFacets.hidden = false;
      fallbackEls.forEach(function (el) { el.hidden = true; });
      root.setAttribute('data-fs-active', 'true');
      releasePending();
      /* Remember the filter row's height so the next page's loading state reserves exactly that space. */
      if (fsFacets) requestAnimationFrame(function () {
        try { sessionStorage.setItem('pf-fs-facets-h-' + (window.innerWidth < 750 ? 'm' : 'd'), String(Math.round(fsFacets.getBoundingClientRect().height))); } catch (e) {}
      });
    }

    function renderProducts(products) {
      renderResultsInto(fsGrid, products, { kind: 'collection', categoryID: categoryID });
    }

    function setNarrow(key, value) {
      if (ui) { ui.setNarrow(key, value); return; }
      state.narrowBy = state.narrowBy.filter(function (n) { return n[0] !== key; });
      if (value != null) state.narrowBy.push([key, value]);
      state.page = 1;
      state.announce = true;
      commitUrl();
      load();
    }

    function wireTierNav(tierKey, tierValues) {
      if (!tierNav || tierNav.getAttribute('data-pf-wired') === 'true') return;
      tierNav.setAttribute('data-pf-wired', 'true');
      var allLink = tierNav.querySelector('[data-pf-tier-all]');
      var tierLinks = tierNav.querySelectorAll('[data-pf-tier]');

      function setActive(link) {
        [allLink].concat(Array.prototype.slice.call(tierLinks)).forEach(function (l) {
          if (!l) return;
          l.classList.toggle('pf-deal-tier--active', l === link);
          if (l === link) l.setAttribute('aria-current', 'page'); else l.removeAttribute('aria-current');
        });
      }

      if (allLink) {
        allLink.addEventListener('click', function (e) {
          e.preventDefault();
          setNarrow(tierKey, null);
          setActive(allLink);
        });
      }

      tierLinks.forEach(function (link) {
        var pct = link.getAttribute('data-pf-tier');
        var tierLabel = 'Sale ' + pct + '%+';
        var hasValue = tierValues.some(function (v) { return v[0] === tierLabel; });
        if (!hasValue) return;
        link.addEventListener('click', function (e) {
          e.preventDefault();
          setNarrow(tierKey, tierLabel);
          setActive(link);
        });
      });
    }

    /**
     * Category shortcut chips (sale page): data-pf-cat = exact Fast Simon "Type"
     * facet value(s) (comma-separated values OR together — FS treats repeated
     * narrowBy entries in the same group as OR, per docs example
     * [["Colour","white"],["Colour","green"]]). data-pf-price = "min-max" for the
     * documented "<base>_from_to" range narrow. Verified live on this store
     * 2026-08-19: [["Type","T-shirts"]] → 393, [["Price_from_to","0-50"]] → 843.
     * Only wired once a facets response confirms the "Type" facet exists;
     * otherwise the chips keep their plain-link fallback behavior.
     */
    function wireCatNav(facets) {
      if (!catNav || catNav.getAttribute('data-pf-wired') === 'true') return;
      var hasType = (facets || []).some(function (f) { return f[0] === 'Type'; });
      if (!hasType) return;
      catNav.setAttribute('data-pf-wired', 'true');

      var links = catNav.querySelectorAll('[data-pf-cat], [data-pf-price]');
      links.forEach(function (link) {
        link.addEventListener('click', function (e) {
          e.preventDefault();
          var wasActive = link.classList.contains('pf-cat-shortcut--active');
          var cat = link.getAttribute('data-pf-cat');
          var priceRange = link.getAttribute('data-pf-price');

          if (cat != null) {
            catNav.querySelectorAll('[data-pf-cat]').forEach(function (l) {
              l.classList.remove('pf-cat-shortcut--active');
            });
            state.narrowBy = state.narrowBy.filter(function (n) { return n[0] !== 'Type'; });
            if (!wasActive) {
              cat.split(',').forEach(function (v) { state.narrowBy.push(['Type', v]); });
              link.classList.add('pf-cat-shortcut--active');
            }
            state.page = 1;
            state.announce = true;
            commitUrl();
            load();
          } else if (priceRange != null) {
            link.classList.toggle('pf-cat-shortcut--active', !wasActive);
            setNarrow('Price_from_to', wasActive ? null : priceRange);
          }
        });
      });
    }

    /* Audit #3 (2026-10-25): the sale page shows the full filter list again next to the chips. Both edit the
       same narrowBy state, so after every load the chips are re-marked from that state: a category chip is lit
       only when the Type filter is exactly its value set, Under $50 only when the price range is exactly 0-50.
       Ticking "Hats" in the Type list lights the Hats chip; clearing it there un-lights it. */
    function syncCatNav() {
      if (!catNav || catNav.getAttribute('data-pf-wired') !== 'true') return;
      var types = state.narrowBy.filter(function (n) { return n[0] === 'Type'; }).map(function (n) { return n[1]; }).sort().join(',');
      var price = null;
      state.narrowBy.forEach(function (n) { if (n[0] === 'Price_from_to') price = String(n[1]); });
      catNav.querySelectorAll('[data-pf-cat]').forEach(function (l) {
        var want = l.getAttribute('data-pf-cat').split(',').sort().join(',');
        l.classList.toggle('pf-cat-shortcut--active', !!types && types === want);
      });
      catNav.querySelectorAll('[data-pf-price]').forEach(function (l) {
        l.classList.toggle('pf-cat-shortcut--active', price === l.getAttribute('data-pf-price'));
      });
    }

    function renderFacets(facets) {
      wireCatNav(facets);
      syncCatNav();
      if (!ui) return;
      if (!facets || !facets.length) { ui.renderFacetList([]); return; }

      var tierFacet = findTierFacet(facets);
      if (tierFacet) wireTierNav(tierFacet[0], tierFacet[1]);

      var remaining = tierFacet ? facets.filter(function (f) { return f !== tierFacet; }) : facets;
      ui.renderFacetList(remaining);
    }

    function renderPagination(response) {
      var existing = root.querySelector('[data-pf-fs-pagination]');
      if (existing) existing.remove();
      var pageCount = response.pageCount || 1;
      if (pageCount <= 1) return;
      var nav = document.createElement('div');
      nav.className = 'pf-fs-pagination';
      nav.setAttribute('data-pf-fs-pagination', '');
      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'pf-btn pf-btn--secondary';
      prev.textContent = 'Prev';
      prev.disabled = state.page <= 1;
      prev.addEventListener('click', function () { state.page -= 1; commitUrl(); load(); root.__pfScrollPending = true; scrollToGrid(root); });
      var indicator = document.createElement('span');
      indicator.className = 'pf-mono pf-fs-pagination__indicator';
      indicator.textContent = 'PAGE ' + state.page + ' / ' + pageCount;
      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'pf-btn pf-btn--secondary';
      next.textContent = 'Next';
      next.disabled = state.page >= pageCount;
      next.addEventListener('click', function () { state.page += 1; commitUrl(); load(); root.__pfScrollPending = true; scrollToGrid(root); });
      nav.appendChild(prev);
      nav.appendChild(indicator);
      nav.appendChild(next);
      fsGrid.insertAdjacentElement('afterend', nav);
    }

    function reportView(response) {
      var ids = reportIds(response.products);
      var data = {
        categoryID: categoryID,
        narrowBy: state.narrowBy,
        products: ids.products,
        rescount: response.totalResults,
        pagenum: response.page
      };
      if (ids.promoTiles) data.promoTiles = ids.promoTiles;
      if (state.userPickedSort && state.sortBy) data.sortBy = state.sortBy;
      fireEvent('SmartCollectionPreformed', data);
    }

    /* TAG URLs (2026-10-25, "Fast Simon for all filtering"): /collections/<handle>/<tag> used to bypass Fast Simon.
       The section now hands the tags over (data-pf-fs-tags); the first facet response maps each tag to its Fast
       Simon tag-filter value (Isp-tag-<Group>, matched by value or "Group value", case/punctuation-insensitive;
       Type and Vendor too). All matched → the view runs on Fast Simon with those filters and the address becomes
       the clean /collections/<handle>?fs.… form. Any tag without a Fast Simon equivalent → the Shopify
       tag-filtered grid stays, because serving it through Fast Simon would show the wrong products. */
    var pendingTags = null;
    try { pendingTags = JSON.parse(root.getAttribute('data-pf-fs-tags') || 'null'); } catch (e) { pendingTags = null; }
    if (pendingTags && !pendingTags.length) pendingTags = null;

    function normTag(x) { return String(x).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim(); }
    function matchTag(tag, facets) {
      var t = normTag(tag);
      /* Review 2026-10-25: Fast Simon tag groups first, then Type, then Vendor (a vendor can share a tag's name). */
      function rank(f) { return /^Isp-tag-/.test(f[0]) ? 0 : (f[0] === 'Type' ? 1 : 2); }
      facets = facets.slice().sort(function (a, b) { return rank(a) - rank(b); });
      for (var i = 0; i < facets.length; i++) {
        var key = facets[i][0], vals = facets[i][1] || [];
        var isTag = /^Isp-tag-/.test(key);
        if (!isTag && key !== 'Type' && key !== 'Vendor') continue;
        var group = isTag ? normTag(key.slice(8)) : '';
        for (var j = 0; j < vals.length; j++) {
          if (!Array.isArray(vals[j])) continue;
          var v = normTag(vals[j][0]);
          if (v === t || (group && group + ' ' + v === t)) return [key, vals[j][0]];
        }
      }
      return null;
    }
    function resolveTags() {
      window.FastSimonSDK.smartCollections({
        categoryID: categoryID, page: 1, productsPerPage: state.productsPerPage, narrowBy: [], facets: true,
        onProductsLoaded: function () {},
        onFacetsLoaded: function (response) {
          var facets = (response && response.facets) || [];
          var mapped = pendingTags.map(function (tag) { return matchTag(tag, facets); });
          pendingTags = null;
          /* Shopify's /tag-a+tag-b means BOTH tags; two values of one Fast Simon filter would mean EITHER. Keep
             Shopify's tag grid when two tags land on the same filter. */
          var keys = {}, sameKey = false;
          mapped.forEach(function (m) { if (m) { if (keys[m[0]]) sameKey = true; keys[m[0]] = 1; } });
          if (sameKey || mapped.some(function (m) { return !m; })) {
            log('A tag on this URL has no Fast Simon filter; the Shopify tag page stays.');
            releasePending();
            return;
          }
          mapped.forEach(function (m) { state.narrowBy.push(m); });
          try { history.replaceState({ pfFs: true }, '', buildUrl(location.pathname.replace(/(\/collections\/[^\/]+)\/.+$/, '$1'), state, TIER_KEY)); } catch (e) {}
          load();
        }
      });
    }

    var newest = newestPager();
    function load() {
      if (pendingTags) { resolveTags(); return; }
      if (state.sortBy === NEWEST_SORT) {
        newest.run(function (a) { window.FastSimonSDK.smartCollections(a); }, { categoryID: categoryID }, state,
          function (response) { renderFacets(response.facets); }, onProducts, loadNative);
        return;
      }
      loadNative();
    }

    /* Fast Simon's own paging (every sort except Newest, and Newest's fallback if a full fetch can't complete). */
    function loadNative() {
      window.FastSimonSDK.smartCollections({
        categoryID: categoryID,
        page: state.page,
        productsPerPage: state.productsPerPage,
        narrowBy: state.narrowBy,
        sortBy: state.sortBy,
        facets: true,
        onFacetsLoaded: function (response) {
          renderFacets(response.facets);
        },
        onProductsLoaded: onProducts
      });
    }

    function onProducts(response) {
      if (!response || !response.products) { releasePending(); return; }
      /* A stale ?page= beyond the last page (e.g. an old shared link): go back to page 1. */
      if (!response.products.length && state.page > 1) {
        state.page = 1;
        try { history.replaceState({ pfFs: true }, '', buildUrl(collectionPath(), state, TIER_KEY)); } catch (e) {}
        load();
        return;
      }
      /* 2026-10-14 (Fast Simon on every collection): if Fast Simon has nothing for this collection on the
         first, unfiltered load (e.g. not indexed yet), keep the server-rendered Liquid grid instead of an
         empty one. Once the FS UI is showing, empty results from a shopper's own filters render normally. */
      if (!response.products.length && root.getAttribute('data-fs-active') !== 'true' && !(state.narrowBy && state.narrowBy.length)) {
        log('Fast Simon returned 0 products for this collection; Liquid grid stays.');
        releasePending();
        return;
      }
      if (revealFsUI(state.announce) === false) return;   // a shopper's own filter/sort click always shows Fast Simon
      renderProducts(response.products);
      renderPagination(response);
      settleScroll(root);
      if (state.announce) { state.announce = false; announce(response.totalResults || 0); }
      reportView(response);
    }

    function markTierChips() {
      if (!tierNav) return;
      var label = null;
      state.narrowBy.forEach(function (n) { if (n[0] === TIER_KEY) label = n[1]; });
      var m = label && label.match(/(\d0)%/);
      var active = m ? tierNav.querySelector('[data-pf-tier="' + m[1] + '"]') : tierNav.querySelector('[data-pf-tier-all]');
      tierNav.querySelectorAll('.pf-deal-tier').forEach(function (l) {
        l.classList.toggle('pf-deal-tier--active', l === active);
        if (l === active) l.setAttribute('aria-current', 'page'); else l.removeAttribute('aria-current');
      });
    }

    if (ui) ui.renderSortControl();
    if (isSale && tierNav) {
      wireTierNav(TIER_KEY, TIER_LABELS.map(function (l) { return [l, 1]; }));
      markTierChips();
    }
    /* Back / Forward: re-apply whatever state the URL holds (tier, filters, sort, page). */
    window.addEventListener('popstate', function () {
      var next = stateFromUrl();
      state.narrowBy = next.narrowBy;
      state.sortBy = next.sortBy;
      state.userPickedSort = next.userPickedSort;
      state.page = next.page;
      state.announce = true;
      markTierChips();
      if (ui) ui.renderSortControl();
      load();
    });
    load();
  }

  function mountSearch(root) {
    var term = root.getAttribute('data-term');
    if (!term) {
      var params = new URLSearchParams(location.search);
      term = params.get('q') || params.get('query') || '';
    }
    term = (term || '').trim();
    if (!term) { releasePending(); return; }

    var fsGrid = root.querySelector('[data-pf-fs-grid]');
    var fallbackEls = root.querySelectorAll('[data-pf-fs-fallback]');
    if (!fsGrid) return;

    /* Audit #2 (2026-10-25): search gets the collection page's filters + sort. The mount is created here when
       the section doesn't render one, so both /search (sections/pf-search.liquid) and /pages/search-results
       (snippets/pf-fastsimon-search-page.liquid) get it; it stays hidden until Fast Simon answers. */
    var fsFacets = root.querySelector('[data-pf-fs-facets]');
    if (!fsFacets) {
      fsFacets = document.createElement('div');
      fsFacets.className = 'pf-fs-facets';
      fsFacets.setAttribute('data-pf-fs-facets', '');
      fsFacets.hidden = true;
      fsGrid.insertAdjacentElement('beforebegin', fsFacets);
    }

    var initial = readUrlState();
    var state = {
      page: initial.page,
      /* Products per page stays fixed across pages and filter changes (Fast Simon paging needs a constant size). */
      productsPerPage: parseInt(root.getAttribute('data-products-per-page'), 10) || 24,
      narrowBy: initial.narrowBy,
      sortBy: initial.sortBy || undefined,
      userPickedSort: !!initial.sortBy,
      announce: false
    };
    function commitUrl() { pushUrl(buildUrl(location.pathname, state, null)); }

    var ui = createFacetUI(fsFacets, { state: state, load: function () { load(); }, onChange: commitUrl, sortOptions: SEARCH_SORTS });
    var announce = createStatus(fsGrid);

    function revealFsUI(force) {
      if (root.getAttribute('data-fs-active') === 'true') return true;
      /* Review 2026-10-25: the first Fast Simon answer can arrive after the 10s failsafe; don't swap the grid under
         a shopper who is already using the fallback page. */
      if (!force && lateTakeoverBlocked()) { log('Late Fast Simon answer; the shopper is using the fallback page, leaving it.'); return false; }
      fsGrid.hidden = false;
      fsFacets.hidden = false;
      fallbackEls.forEach(function (el) { el.hidden = true; });
      root.setAttribute('data-fs-active', 'true');
      releasePending();
      var meta = root.querySelector('[data-pf-fs-meta]');
      if (meta) meta.hidden = false;
    }

    function renderProducts(products) {
      renderResultsInto(fsGrid, products, { kind: 'search', term: term });
    }

    function reportView(response) {
      var ids = reportIds(response.products);
      var data = {
        query: term,
        narrowBy: state.narrowBy,
        products: ids.products,
        rescount: response.totalResults,
        pagenum: response.page
      };
      if (ids.promoTiles) data.promoTiles = ids.promoTiles;
      if (state.userPickedSort && state.sortBy) data.sortBy = state.sortBy;
      fireEvent('SearchPerformed', data);
    }

    function updateMeta(response) {
      var meta = root.querySelector('[data-pf-fs-meta]');
      if (meta) meta.textContent = '"' + term + '" — ' + (response.totalResults || 0) + ' RESULTS';
    }

    // SEARCH PAGINATION (2026-10-03, live review: "32 RESULTS" but only page 1 of 24 was reachable)
    function renderSearchPagination(response) {
      var existing = root.querySelector('[data-pf-fs-pagination]');
      if (existing) existing.remove();
      var perPage = state.productsPerPage || 24;
      var pageCount = response.pageCount || Math.ceil((response.totalResults || 0) / perPage) || 1;
      if (pageCount <= 1) return;
      var nav = document.createElement('div');
      nav.className = 'pf-fs-pagination';
      nav.setAttribute('data-pf-fs-pagination', '');
      function go(delta) {
        state.page += delta;
        commitUrl();
        load();
        root.__pfScrollPending = true;
        scrollToGrid(root);
      }
      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'pf-btn pf-btn--secondary';
      prev.textContent = 'Prev';
      prev.disabled = state.page <= 1;
      prev.addEventListener('click', function () { go(-1); });
      var indicator = document.createElement('span');
      indicator.className = 'pf-mono pf-fs-pagination__indicator';
      indicator.textContent = 'PAGE ' + state.page + ' / ' + pageCount;
      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'pf-btn pf-btn--secondary';
      next.textContent = 'Next';
      next.disabled = state.page >= pageCount;
      next.addEventListener('click', function () { go(1); });
      nav.appendChild(prev);
      nav.appendChild(indicator);
      nav.appendChild(next);
      fsGrid.insertAdjacentElement('afterend', nav);
    }

    var newest = newestPager();
    function load() {
      if (state.sortBy === NEWEST_SORT) {
        newest.run(function (a) { window.FastSimonSDK.fullTextSearch(a); }, { term: term }, state,
          function (response) { ui.renderFacetList(response && response.facets); }, onProducts, loadNative);
        return;
      }
      loadNative();
    }

    function loadNative() {
      window.FastSimonSDK.fullTextSearch({
        term: term,
        page: state.page,
        productsPerPage: state.productsPerPage,
        narrowBy: state.narrowBy,
        sortBy: state.sortBy,
        facets: true,
        onFacetsLoaded: function (response) {
          ui.renderFacetList(response && response.facets);
        },
        onProductsLoaded: onProducts
      });
    }

    function onProducts(response) {
      // Turbolinks (Fast Simon > Autocomplete > Turbolinks): the API returns no products and
      // a target URL for exact-match terms (e.g. "amr" -> /collections/aston-martin). Follow it.
      // Redirects still run first, on the shopper's initial query only (never after a filter/sort change).
      var tl = response && response.turbolink;
      if (tl && tl.u && state.page === 1 && !state.narrowBy.length && !state.userPickedSort) {
        try {
          var dest = new URL(tl.u, window.location.origin);
          if (dest.origin === window.location.origin) { window.location.replace(dest.href); return; }
        } catch (err) {}
      }
      if (!response || !response.products) { releasePending(); return; }
      if (revealFsUI(state.announce) === false) return;   // a shopper's own filter/sort click always shows Fast Simon
      updateMeta(response);
      renderProducts(response.products);
      renderSearchPagination(response);
      settleScroll(root);
      if (state.announce) { state.announce = false; announce(response.totalResults || 0); }
      reportView(response);
    }

    window.addEventListener('popstate', function () {
      var u = readUrlState();
      var q = new URLSearchParams(location.search);
      if ((q.get('q') || q.get('query') || '').trim() !== term) { location.reload(); return; }
      state.narrowBy = u.narrowBy;
      state.sortBy = u.sortBy || undefined;
      state.userPickedSort = !!u.sortBy;
      state.page = u.page;
      state.announce = true;
      ui.renderSortControl();
      load();
    });

    ui.renderSortControl();
    load();
  }

  /* Audit #5 (2026-10-25): "cartVisited" — sent once each time the cart (Rebuy Smart Cart, or /cart) opens. The
     listener lives in assets/pf-fastsimon-shadow.js (loaded on every page); on pages that run this file it calls
     this, so the SDK instance already on the page sends it. Fast Simon's Shopify pixel covers adds and orders. */
  function reportCartVisited() {
    ensureSdk();
    onSdkReady(function () { fireEvent('CartVisited', {}); });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!CART_ADD_RE.test(form.action)) return;
    var productField = form.querySelector('[name="product-id"], [data-product-id]');
    var idField = form.querySelector('[name="id"]');
    var productID = productField
      ? (productField.value || productField.getAttribute('data-product-id'))
      : (idField ? idField.value : null);
    if (!productID) return;
    var variantField = form.querySelector('[name="id"]');
    var payload = { productID: String(productID) };
    if (variantField && variantField.value && String(variantField.value) !== String(productID)) payload.variantID = String(variantField.value);
    ensureSdk(); /* product pages load the SDK lazily; make sure it is coming before queueing the report */
    onSdkReady(function () {
      fireEvent('AddToCartPerformed', payload);
    });
  }, true);

  /* Product clicks are MPA "post-load" events: the SDK saves them in localStorage ("post-load-event") and
     sends them on the next page where the SDK initialises. Product pages now load this file too, so pending
     clicks are flushed on arrival instead of waiting for the shopper's next collection/search page. The SDK
     is only fetched on those pages when something is pending (or on add to cart), to keep PDPs light. */
  function hasPendingFsEvents() {
    try { return !!localStorage.getItem('post-load-event'); } catch (e) { return false; }
  }

  /* EARLY MOUNT (2026-10-25): snippets/pf-fastsimon-boot.liquid also loads this file async from <head>, and
     sections/pf-collection.liquid marks its root data-pf-fs-ready (inline, right after its markup) and calls
     mountReady(). So a collection mounts as soon as its own markup is parsed and the SDK is ready, instead of
     waiting for the whole page (DOMContentLoaded was ~5s on the sale page). Anything else mounts at
     DOMContentLoaded as before. data-pf-fs-mounted prevents a double mount. */
  var FS_ROOTS = '[data-pf-fastsimon-collection], [data-pf-fastsimon-search]';
  function mountReady(force) {
    var roots = Array.prototype.filter.call(document.querySelectorAll(FS_ROOTS), function (r) {
      return !r.hasAttribute('data-pf-fs-mounted') && (force || r.hasAttribute('data-pf-fs-ready'));
    });
    if (!roots.length) return;
    roots.forEach(function (r) { r.setAttribute('data-pf-fs-mounted', ''); });
    ensureSdk();
    onSdkReady(function () {
      if (lateTakeoverBlocked()) { log('Fast Simon arrived after the shopper started using the fallback; leaving it.'); return; }
      roots.forEach(function (r) {
        if (r.hasAttribute('data-pf-fastsimon-collection')) mountCollection(r); else mountSearch(r);
      });
    });
  }

  function initAll() {
    if (!document.querySelector(FS_ROOTS)) {
      if (document.querySelector('[data-pf-fastsimon-track]') && hasPendingFsEvents()) ensureSdk();
      return;
    }
    mountReady(true);
  }

  window.PFFastSimon = { mountCollection: mountCollection, mountSearch: mountSearch, mountReady: mountReady, ensureSdk: ensureSdk, reportCartVisited: reportCartVisited };

  mountReady(false);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();